// AI Generated Component
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { orderBy, safeClone } from '@client/app/shared/utils/object-utils';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { TextSearchService } from '@monsido/ng2/services/text-search/text-search.service';
import { POLICY_EXCHANGE_CENTER_DB } from '@client/app/modules/global-policy/constants/policy-exchange-center-db.data';

@Component({
    selector: 'policy-exchange-center',
    standalone: false,
    template: `
        <div class="policy-exchange-center">
            <div class="row align-items-stretch bg-white">
                <div class="col-48 col-md-36 col-lg-38 pr-md-0 overflow-hidden">
                    <div class="form-group exchange-center-search">
                        <label class="sr-only" for="policy_exchange_center_search" translate>
                            Search
                        </label>
                        <div class="input-group">
                            <div class="input-group-prepend"><span class="input-group-text"><span class="fas fa-search" aria-hidden="true"></span></span></div>
                            <input data-test="search-policy"
                                   [placeholder]="'Search for a predefined policy in the Policy Exchange Center' | translate"
                                   id="search"
                                   (ngModelChange)="onSearch()"
                                   class="form-control"
                                   type="search"
                                   name="search"
                                   [(ngModel)]="search"/>
                        </div>
                    </div>
                </div>
                <div class="col-48 col-md-12 col-lg-10 pl-md-0 d-flex align-items-center">
                    <p class="text-secondary-4 pr-3 my-0" *ngIf="hasTableFilter" translate>
                        Filter
                    </p>
                    <div class="cursor-pointer" *ngIf="hasTableFilter">
                        <span role="button"
                              (click)="showDropdown = !showDropdown"
                              id="policy-template-filter-dropdown"
                              [attr.aria-label]="'Filter dropdown' | translate"
                              class="fa-fw fas fa-filter fa-2x text-link">
                        </span>
                    </div>
                    <div class="table-filters-selector mr-auto pt-3">
                        <table-filters-selector class="table-filters-dropdown ng-hide"
                                                [hidden]="!showTableFilter"
                                                *ngIf="hasTableFilter"
                                                [(showDropdown)]="showDropdown"
                                                [model]="filters"
                                                [filters]="availableFilters"
                                                (onChanges)="sortChange($event.changes)">
                        </table-filters-selector>
                    </div>
                    <div class="d-flex align-items-center pl-5 pr-4">
                        <p class="text-secondary-4 pr-3 my-0" translate>View</p>
                        <div class="pr-3" [ngClass]="{'text-link': templateView === 'grid'}">
                            <span (click)="selectTemplateView('grid')"
                                  [attr.aria-label]="'Grid view' | translate"
                                  class="fa-fw fas fa-th fa-2x">
                            </span>
                        </div>
                        <div [ngClass]="{'text-link': templateView === 'list'}">
                            <span (click)="selectTemplateView('list')"
                                  [attr.aria-label]="'List view' | translate"
                                  class="fa-fw fas fa-bars fa-2x">
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row mt-4">
                <div class="offset-sm-24 offset-md-10 pl-sm-0 col">
                    <policy-exchange-center-labels [labels]="filters" (onCloseLabel)="removeLabel($event)">
                    </policy-exchange-center-labels>
                </div>
            </div>
            <div class="row">
                <div class="col-48 col-sm-24 col-md-10 pt-6">
                    <div class="row">
                        <div class="col-48">
                            <div data-test="custom-policy-button"
                                 class="btn border-color-secondary-5 btn-border-radius btn-secondary flat button-container border-dashed border-size-3"
                                 (click)="setEmptyPolicy()">
                                <h4 class="mt-9" translate>Create your own policy</h4>
                                <br/>
                                <p class="text-secondary-4" translate>Build it from scratch</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-48 col-sm-24 col-md-38 pt-6 pl-sm-0 pr-sm-6">
                    <policy-exchange-center-views-grid [templates]="templates"
                                                       class="ng-hide"
                                                       [hidden]="templateView !== 'grid'"
                                                       (chooseTemplate)="onChooseTemplate($event)">
                    </policy-exchange-center-views-grid>
                    <policy-exchange-center-views-list [templates]="templates"
                                                       class="ng-hide"
                                                       [hidden]="templateView !== 'list'"
                                                       (chooseTemplate)="onChooseTemplate($event)">
                    </policy-exchange-center-views-list>
                </div>
            </div>
        </div>
    `,
})
export class PolicyExchangeCenterComponent implements OnInit {
    @Output() chooseTemplate = new EventEmitter<any>();

    hasTableFilter = false;
    showTableFilter = false;
    showDropdown = false;
    templateView = 'grid';
    originalTemplates: any[] = [];
    filters: any = {};
    search = '';
    templates: any[] = [];
    availableFilters: any = {};

    constructor (
        private activeFeatureService: ActiveFeatureService,
        private translateService: TranslateService,
        private textSearchService: TextSearchService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    private activate (): void {
        this.hasTableFilter = this.activeFeatureService.isFeatureActive('table_filters');
        this.showTableFilter = false;
        this.showDropdown = false;
        this.templateView = 'grid';
        this.originalTemplates = safeClone(POLICY_EXCHANGE_CENTER_DB);
        this.filters = {};
        this.filters[this.translateService.getString('Sort by', undefined, 'Exchange center filter option')] = [];
        // TODO: Reimplement Region & Vertical https://zube.io/monsido/monsido/c/21967
        // this.filters[this.translateService.getString("Vertical", "Exchange center filter option")] = [];
        // this.filters[this.translateService.getString("Region", "Exchange center filter option")] = [];
        setTimeout(() => {
            // We do this because the dropdown is shown on init, then disappears right afterwards
            this.showTableFilter = true;
        });
        this.getTemplates();
        this.setDropdownOptions();
    }

    onChooseTemplate (event: any): void {
        this.chooseTemplate.emit(event.template);
    }

    setEmptyPolicy (): void {
        const policy = {
            rules: [],
            targets: [],
            tags: [],
            labels: [],
        };
        this.filters = {};
        this.search = this.search ? this.search : '';
        this.chooseTemplate.emit({ template: { policy: policy } });
    }

    removeLabel (event: any): void {
        this.filters[event.group].splice(event.index, 1);
        this.sortChange(this.filters);
    }

    selectTemplateView (view: string): void {
        this.templateView = view;
    }

    onSearch (): void {
        this.templates = this.originalTemplates.filter((template) => {
            if (template.name.toLowerCase().includes(this.search.toLowerCase())) {
                return template;
            }
        });
    }

    getTemplates (): void {
        this.templates = this.originalTemplates.map((template) => {
            template.tags = template.policy.tags
                .map((tag: any) => {
                    return this.tagOption(tag);
                })
                .join(', ');
            return template;
        });
    }

    sortChange (filters: any): void {
        this.filters = filters;
        const search = typeof this.search === 'string' ? this.search : '';
        const sortBy = filters[this.translateService.getString('Sort by', undefined, 'Exchange center filter option')]?.map((item: any) => item.value) || [];
        // TODO: Reimplement Region & Vertical https://zube.io/monsido/monsido/c/21967
        // const regions = filters[this.translateService.getString("Region", "Exchange center filter option")]?.map((item: any) => item.value) || [];
        // const verticals = filters[this.translateService.getString("Vertical", "Exchange center filter option")]?.map((item: any) => item.value) || [];

        const sortDirections = sortBy.map((sort: string) => {
            return sort === 'name' ? 'asc' : 'desc';
        });

        const filteredTemplates = this.originalTemplates.filter((template) => {
            // TODO: Reimplement Region & Vertical https://zube.io/monsido/monsido/c/21967
            /** if (verticals.length > 0 && !this.hasVertical(template, verticals)) {
                return false;
            }

            if (regions.length > 0 && !this.hasRegion(template, regions)) {
                return false;
            }**/
            const haystack = template && template.name;

            if (typeof haystack !== 'string') {
                return false;
            }

            return this.textSearchService.fuzzySearch(search, haystack);
        });

        this.templates = orderBy(filteredTemplates, sortBy, sortDirections);
    }

    // PROTECTED

    private tagOption (tag: string): string | null {
        switch (tag) {
            case 'qa':
                return this.translateService.getString('QA');
            case 'seo':
                return this.translateService.getString('SEO Issues');
            case 'accessibility':
                return this.translateService.getString('Accessibility');
            default:
                return null;
        }
    }

    private hasVertical (template: any, verticals: any[]): boolean {
        return template.vertical?.some((vertical: any) => verticals.indexOf(vertical) > -1) || false;
    }

    private hasRegion (template: any, regions: any[]): boolean {
        return template.country?.some((region: any) => regions.indexOf(region) > -1) || false;
    }

    private setDropdownOptions (): void {
        this.availableFilters = {};
        this.availableFilters[this.translateService.getString('Sort by', undefined, 'Exchange center filter option')] = [
            {
                name: this.translateService.getString('Name', undefined, 'Exchange Center Sort by option'),
                value: 'name',
            },
            {
                name: this.translateService.getString('Newest', undefined, 'Exchange Center Sort by option'),
                value: 'created_at',
            },
        ];
        // TODO: Reimplement Region & Vertical https://zube.io/monsido/monsido/c/21967
        // this.availableFilters[this.translateService.getString("Vertical", "Exchange center filter option")] = [
        //     {
        //         name: this.translateService.getString("Finance", "Exchange Center Vertical option"),
        //         value: "finance",
        //     },
        //     {
        //         name: this.translateService.getString("Education", "Exchange Center Vertical option"),
        //         value: "education",
        //     },
        //     {
        //         name: this.translateService.getString("Government", "Exchange Center Vertical option"),
        //         value: "government",
        //     },
        // ];
        // this.availableFilters[this.translateService.getString("Region", "Exchange center filter option")] = [
        //     {
        //         name: this.translateService.getString("North America", "Exchange Center Region option"),
        //         value: "north_america",
        //     },
        //     {
        //         name: this.translateService.getString("Europe", "Exchange Center Region option"),
        //         value: "europe",
        //     },
        //     {
        //         name: this.translateService.getString("Africa", "Exchange Center Region option"),
        //         value: "africa",
        //     },
        //     {
        //         name: this.translateService.getString("Asia", "Exchange Center Region option"),
        //         value: "asia",
        //     },
        // ];
    }
}
